import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField } from '@angular/forms/signals';
import { SignalCheckbox, SignalFormDirective, SignalTextField } from '@vallift/ngl';
import type { FormField } from '../../../../models/built-form';

interface AddressSelectorConfigModel {
  label: string;
  helpText: string;
  placeholder: string;
  required: boolean;
}

@Component({
  selector: 'app-address-selector-config-view',
  standalone: true,
  templateUrl: './address-selector-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SignalFormField, SignalTextField, SignalCheckbox, SignalFormDirective],
})
export class AddressSelectorConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<AddressSelectorConfigModel>({
    label: '',
    helpText: '',
    placeholder: '',
    required: false,
  });

  readonly configForm = form(this.model);
  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        placeholder: next.placeholder ?? '',
        required: next.required ?? validation.required ?? false,
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label.trim(),
        helpText: draft.helpText.trim() || undefined,
        placeholder: draft.placeholder ?? '',
        required: draft.required,
        validation: {
          required: draft.required,
        },
      });
    });
  }
}
